// Typed event surface for the M6 overlay interactions.
//
// Generic over the event map so consumers get type-safe on/off/emit. The
// concrete M6 event map (ViewerEventMap) lives at the bottom — host code (the
// variant-B overlay, main.ts, eventually a `<quidities-viewer>` web component
// host) subscribes to these strings.
//
// Cart events fire "additively" — the viewer doesn't own cart state. The
// host is expected to translate `cart:add` / `cart:remove` into whatever
// downstream behaviour fits (Shopify.addItem, Odoo quote, local stub, …).

import type { Anchor } from './viewer-controller.js';

export class EventBus<M> {
  private handlers = new Map<keyof M, Set<(payload: M[keyof M]) => void>>();

  on<K extends keyof M>(event: K, cb: (payload: M[K]) => void): () => void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(cb as (payload: M[keyof M]) => void);
    return () => {
      set?.delete(cb as (payload: M[keyof M]) => void);
    };
  }

  emit<K extends keyof M>(event: K, payload: M[K]): void {
    const set = this.handlers.get(event);
    if (!set) return;
    for (const cb of set) (cb as (p: M[K]) => void)(payload);
  }

  clear(): void {
    this.handlers.clear();
  }
}

export interface CartLineItem {
  /** idiagram node id of the part. */
  id: string;
  /** SKU / part number — what the host will send to its storefront. */
  sku: string;
  /** Display name for the line item. */
  name: string;
  /** Quantity added in this event (positive integer). */
  qty: number;
  /** Unit price at the moment of the add — informational; the host's
   *  storefront is the source of truth for pricing at checkout. */
  unitPrice: number;
  currency: string;
}

export interface ViewerEventMap {
  /** Hover entered a part anchor. `null` when hover left without entering another. */
  'part:hover': { id: string; anchor: Anchor } | { id: null };
  /** A part became active (selected). `prev` is the previously active id, if any. */
  'part:select': { id: string; anchor: Anchor; prev: string | null };
  /** No part is active any more (e.g. close button on the edge card). */
  'part:deselect': { prev: string };
  /** Host should add this line to the cart. */
  'cart:add': CartLineItem;
  /** Host should remove this part from the cart. */
  'cart:remove': { id: string };
  /** Host should reveal the cart drawer (variant F in the designer mocks). */
  'cart:open': Record<string, never>;
  /** Host should run the checkout/signup flow (variant G). */
  'checkout:request': { lineCount: number; total: number; currency: string };
  /** User adjusted the explode slider. Mirrors `viewer.explode(factor)`. */
  'view:explode': { factor: number };
  /** User reset the explode + camera to fit. */
  'view:reset': Record<string, never>;
}

export type ViewerBus = EventBus<ViewerEventMap>;

export function createViewerBus(): ViewerBus {
  return new EventBus<ViewerEventMap>();
}
